import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Upload,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  FileExclamationOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const AddContentComplaints = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await masterService.addContentComplaint(values);

      if (response.data && response.data.status) {
        notify.success({
          message:
            response.data.message || "Content complaint added successfully",
        });
        form.resetFields();
        setFileList([]);
        navigate("/content-compliance");
      } else {
        notify.error({
          message: response.data.message || "Failed to add content complaint",
        });
      }
    } catch (error) {
      console.error("Error adding content complaint:", error);
      notify.error({
        message: error.data.message || "Error adding content complaint",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setFileList([]);
  };

  const handleGoBack = () => {
    navigate("/content-compliance");
  };

  useEffect(() => {
    setPageTitle("Add Content Compliance");
  }, [setPageTitle]);

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("pdf", file);

    try {
      const response = await masterService.uploadPdf(formData);
      console.log("responsepdf===>", response.data.data.s3_key);
      if (response.data && response.data.status) {
        console.log("responsepdf1===>", response.data.data.s3_key);
        form.setFieldsValue({
          content_url: response.data.data.s3_key,
        });
        notify.success({ message: "File uploaded successfully" });
        return false; // Prevent default upload behavior
      } else {
        notify.error({ message: "Failed to upload file" });
        return false;
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      notify.error({ message: "Error uploading file" });
      return false;
    }
  };

  const uploadProps = {
    beforeUpload: handleFileUpload,
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    accept: ".pdf",
    maxCount: 1,
  };

  return (
    <div className="min-h-screen">
      <Card
      >
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={{
            is_active: "Y",
          }}
          className="space-y-6"
        >
          {/* Content Complaints Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <FileExclamationOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Content Complaints Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Content Name
                    </span>
                  }
                  name="content_name"
                  rules={[
                    { required: true, message: "Please enter content name" },
                    { validator: noWhitespaceValidator },
                  ]}
                >
                  <Input
                    placeholder="Enter content name"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Status
                    </span>
                  }
                  name="is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Hidden field to store the uploaded PDF URL string */}
            <Form.Item
              name="content_url"
              rules={[{ required: true, message: "Please upload a PDF" }]}
              style={{ display: "none" }}
            >
              <Input />
            </Form.Item>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Upload PDF
                    </span>
                  }
                >
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />} className="rounded-lg">
                      Upload PDF
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Adding..." : "Add Content Complaints"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddContentComplaints;
